import { useEffect, useState } from 'react';

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const fieldClass =
    'w-full rounded-2xl border border-[#D9EEFF] bg-white px-4 py-3 text-[#0F172A] placeholder:text-[#0F172A]/45 focus:outline-none focus:ring-2 focus:ring-[#B9DEFF] focus:border-[#027FDB] transition-colors';

  useEffect(() => {
    if (submitStatus === 'idle') return;

    const timeout = window.setTimeout(() => {
      setSubmitStatus('idle');
    }, 5000);

    return () => window.clearTimeout(timeout);
  }, [submitStatus]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      name: formData.get('name')?.toString() || '',
      phone: formData.get('phone')?.toString() || '',
      email: formData.get('email')?.toString() || '',
      service: formData.get('service')?.toString() || '',
      pflegegrad: formData.get('pflegegrad')?.toString() || '',
      message: formData.get('message')?.toString() || '',
      consent: formData.get('consent') === 'on',
    };

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Request failed');
      }

      setSubmitStatus('success');
      form.reset();
    } catch (err) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="relative overflow-hidden py-20 md:py-24 bg-white text-[#0F172A]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-0 top-8 h-48 w-48 rounded-full bg-[#93C5FD]/20 blur-3xl" />
        <div className="absolute right-0 bottom-0 h-56 w-56 rounded-full bg-[#DBEAFE]/70 blur-3xl" />
      </div>

      <div className="relative max-w-[1180px] mx-auto px-5 sm:px-6 lg:px-8">
        <div className="mb-10 md:mb-12 max-w-3xl text-center mx-auto">
          <p className="inline-flex items-center rounded-full border border-[#D9EEFF] bg-[#EAF4FF] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#027FDB] shadow-[0_8px_24px_rgba(2,127,219,0.08)]">
            Kontakt & Anfrage
          </p>
          <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight text-[#0F172A]">
            Unverbindliche Erstberatung anfragen
          </h2>
          <p className="mt-5 text-[0.98rem] md:text-[1.05rem] leading-relaxed text-[#0F172A]/75">
            Schildern Sie uns kurz Ihr Anliegen. Wir melden uns in der Regel innerhalb von 24 Stunden zurück und klären gemeinsam die passende Unterstützung für Ihren Alltag in Berlin.
          </p>
        </div>

        <div className="grid gap-5 md:gap-6 lg:grid-cols-12 items-start lg:gap-7">
          <div className="lg:col-span-4 space-y-5">
            <div className="rounded-[30px] border border-[#D9EEFF] bg-[linear-gradient(145deg,rgba(255,255,255,0.98),rgba(234,244,255,0.95),rgba(217,238,255,0.85))] p-6 md:p-7 shadow-[0_20px_48px_rgba(15,23,42,0.05),0_8px_22px_rgba(2,127,219,0.07)]">
              <div className="flex items-center gap-3 mb-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EAF4FF] text-[#027FDB] text-xl border border-[#D9EEFF]">
                  <i className="ri-customer-service-2-line" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0F172A]">Persönliche Beratung</p>
                  <p className="text-sm text-[#0F172A]/65">Klar, persönlich und unverbindlich</p>
                </div>
              </div>

              <p className="text-sm md:text-[0.98rem] leading-relaxed text-[#0F172A]/75">
                Ob erste Fragen oder konkrete Unterstützung: Wir klären schnell, welche Hilfe sinnvoll ist und wie der nächste Schritt konkret aussieht.
              </p>

              <div className="mt-6 space-y-3">
                <a
                  href="tel:+4915229990328"
                  className="group flex items-center justify-between rounded-2xl border border-[#D9EEFF] bg-white px-5 py-4 shadow-[0_12px_28px_rgba(2,127,219,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(2,127,219,0.12)]"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#EAF4FF] text-[#027FDB] text-lg border border-[#D9EEFF]">
                      <i className="ri-phone-line" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#0F172A]/55">Anrufen</p>
                      <p className="text-sm md:text-base font-semibold text-[#0F172A]">+49 152 29990328</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-up-line text-lg text-[#027FDB] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>

                <a
                  href="mailto:info@alltagsstern.com"
                  className="group flex items-center justify-between rounded-2xl border border-[#D9EEFF] bg-white px-5 py-4 shadow-[0_12px_28px_rgba(2,127,219,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(2,127,219,0.12)]"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#EAF4FF] text-[#027FDB] text-lg border border-[#D9EEFF]">
                      <i className="ri-mail-line" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#0F172A]/55">E-Mail direkt</p>
                      <p className="text-sm md:text-base font-semibold text-[#0F172A] break-all sm:break-normal">info@alltagsstern.com</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-up-line text-lg text-[#027FDB] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>

            <div className="rounded-[26px] border border-[#D9EEFF] bg-white p-5 md:p-5.5 shadow-[0_14px_30px_rgba(15,23,42,0.045)]">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#EAF4FF] text-[#027FDB] text-lg border border-[#D9EEFF] shrink-0">
                  <i className="ri-time-line" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#0F172A]">In der Regel Rückmeldung innerhalb von 24 Stunden</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#0F172A]/70">
                    Berlinweit, unverbindlich und mit klarer Einschätzung, welche Unterstützung kurzfristig sinnvoll ist.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 rounded-[32px] border border-[#D9EEFF] bg-[linear-gradient(145deg,rgba(255,255,255,0.98),rgba(234,244,255,0.94),rgba(217,238,255,0.82))] p-5 md:p-7 shadow-[0_24px_60px_rgba(15,23,42,0.06),0_10px_28px_rgba(2,127,219,0.08)]">
            <form className="grid gap-4 md:gap-4.5 md:grid-cols-2" onSubmit={handleSubmit}>
              <div className="md:col-span-2 rounded-2xl border border-[#D9EEFF] bg-white px-4 py-3.5 text-sm leading-relaxed text-[#0F172A]/70 shadow-[0_10px_24px_rgba(2,127,219,0.05)]">
                Wir melden uns in der Regel innerhalb von 24 Stunden zurück. Hilfreich sind Angaben zur gewünschten Unterstützung, zur betroffenen Person und eine Telefonnummer für die Rückmeldung.
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-[#0F172A]">Name*</label>
                <input type="text" required className={fieldClass} name="name" />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-[#0F172A]">Telefon*</label>
                <input type="tel" required className={fieldClass} name="phone" />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-[#0F172A]">E-Mail (optional)</label>
                <input type="email" className={fieldClass} name="email" />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-[#0F172A]">Gewünschte Unterstützung*</label>
                <select required className={fieldClass} name="service" style={{ backgroundPosition: 'right 1rem center' }}>
                  <option value="">Bitte auswählen</option>
                  <option value="wohnungsreinigung">Wohnungsreinigung</option>
                  <option value="waeschepflege">Wäschepflege</option>
                  <option value="fensterreinigung">Fensterreinigung</option>
                  <option value="einkaufen">Einkaufen</option>
                  <option value="termine-begleiten">Termine begleiten</option>
                  <option value="botengaenge">Botengänge</option>
                  <option value="kochen">Kochen</option>
                  <option value="mobilitaetshilfe">Mobilitätshilfe</option>
                  <option value="gesellschaft-leisten">Gesellschaft leisten</option>
                  <option value="alltagsorganisation">Alltagsorganisation</option>
                  <option value="sonstiges">Sonstiges Anliegen</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-[#0F172A]">Pflegegrad (optional)</label>
                <select className={fieldClass} name="pflegegrad" style={{ backgroundPosition: 'right 1rem center' }}>
                  <option value="">Bitte auswählen</option>
                  <option value="pflegegrad-1">Pflegegrad 1</option>
                  <option value="pflegegrad-2">Pflegegrad 2</option>
                  <option value="pflegegrad-3">Pflegegrad 3</option>
                  <option value="pflegegrad-4">Pflegegrad 4</option>
                  <option value="pflegegrad-5">Pflegegrad 5</option>
                  <option value="noch-kein-pflegegrad">Noch kein Pflegegrad</option>
                  <option value="unbekannt">Unbekannt</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-[#0F172A]">Nachricht*</label>
                <textarea
                  required
                  rows={5}
                  className={fieldClass}
                  name="message"
                  placeholder="z. B. welche Unterstützung benötigt wird, in welchem Berliner Bezirk die Hilfe gebraucht wird, für wen sie gedacht ist, welcher Pflegegrad vorliegt und wann wir Sie am besten erreichen können."
                />
              </div>

              <div className="md:col-span-2 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                <label className="flex max-w-md items-start gap-3 text-xs leading-relaxed text-[#0F172A]/65">
                  <input
                    type="checkbox"
                    name="consent"
                    required
                    className="mt-1 h-4 w-4 rounded border-[#B9DEFF]"
                  />
                  <span>
                    Ich habe die{' '}
                    <a
                      href="/datenschutz"
                      className="font-medium text-[#027FDB] underline underline-offset-4 transition-colors hover:text-[#026BB8]"
                    >
                      Datenschutzerklärung
                    </a>{' '}
                    gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung der Anfrage zu.
                  </span>
                </label>

                <button
                  type="submit"
                  className="inline-flex w-full sm:w-auto min-w-[290px] items-center justify-center gap-2.5 rounded-full border border-[#027FDB] bg-[linear-gradient(135deg,#027FDB,#0A8CFF,#49A9FF)] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white shadow-[0_14px_34px_rgba(2,127,219,0.20)] transition-all duration-300 hover:scale-[1.02] hover:border-[#026BB8] hover:shadow-[0_18px_42px_rgba(2,127,219,0.24)] disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={isSubmitting}
                >
                  <i className="ri-send-plane-fill text-base" aria-hidden="true" />
                  {isSubmitting ? 'Wird gesendet…' : 'Unverbindliche Anfrage senden'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="md:col-span-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns schnellstmöglich zurück. Alternativ erreichen Sie uns direkt unter{' '}
                  <a href="mailto:info@alltagsstern.com" className="font-medium underline underline-offset-4 hover:no-underline">
                    info@alltagsstern.com
                  </a>
                  .
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="md:col-span-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt unter{' '}
                  <a href="mailto:info@alltagsstern.com" className="font-medium underline underline-offset-4 hover:no-underline">
                    info@alltagsstern.com
                  </a>
                  .
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
